import type { CameraApiResponse } from "@src/services/api/cameras-api";
import type { TenantScenarioStatusItem } from "@visioni/a3s-traffic-flow/fleet-messages";
import { getCameraDisplayName, getCameraKeyString, getSiteName, formatCameraLabel } from "@src/utils/key-parser";
import { capitalize } from "@src/utils/capitalize";
import type { ScenarioConfig } from "../stores/scenario-coordinates-store";

export interface ScenarioRow {
  key: string;
  siteId: string;
  scenarioUuid: string;
  /** Display name — friendly name override if set, else the backend `scenarioName`. */
  scenarioName: string;
  /** Original backend name, kept for reference (e.g. "rename" affordance default). */
  backendScenarioName: string;
  cameraKey: string;
  cameraDisplayName: string;
  cameraSubtitle: string;
  updatedAt: string;
}

export interface ScenarioSiteGroup {
  siteId: string;
  siteName: string;
  siteSubtitle?: string;
  rows: ScenarioRow[];
}

export function groupScenariosBySite(
  items: TenantScenarioStatusItem[],
  cameras: CameraApiResponse[],
  configByUuid: Record<string, ScenarioConfig> = {},
): ScenarioSiteGroup[] {
  if (items.length === 0) return [];

  const cameraIndex = new Map<string, CameraApiResponse>();
  for (const camera of cameras) {
    cameraIndex.set(`${camera.key.asset}@${camera.key.id}`, camera);
  }

  const groups = new Map<string, ScenarioSiteGroup>();
  const orderedGroups: ScenarioSiteGroup[] = [];

  for (const item of items) {
    const cameraKey = `${item.asset}@${item.camera}`;
    const camera = cameraIndex.get(cameraKey);
    if (!camera) {
      console.warn(`[traffic-flow] orphan camera in scenario list: ${cameraKey}`);
      continue;
    }

    const siteId = String(item.site);
    let group = groups.get(siteId);
    if (!group) {
      const { name, subtitle } = getSiteName(camera, siteId);
      group = { siteId, siteName: name, siteSubtitle: subtitle, rows: [] };
      groups.set(siteId, group);
      orderedGroups.push(group);
    }

    const cameraKeyString = getCameraKeyString(camera);
    const cameraDisplayName = capitalize(camera.name || getCameraDisplayName(cameraKeyString));
    const cameraSubtitle = formatCameraLabel(cameraKeyString);

    for (const scenario of item.scenarios) {
      const friendly = configByUuid[scenario.scenarioUuid]?.friendlyName;
      group.rows.push({
        key: `${cameraKeyString}:${scenario.scenarioUuid}`,
        siteId,
        scenarioUuid: scenario.scenarioUuid,
        scenarioName: friendly && friendly.trim() !== "" ? friendly : scenario.scenarioName,
        backendScenarioName: scenario.scenarioName,
        cameraKey: cameraKeyString,
        cameraDisplayName,
        cameraSubtitle,
        updatedAt: item.updatedAt,
      });
    }
  }

  return orderedGroups;
}
